/**
 * Hit testing for eraser tool. Finds which stroke (if any) was clicked.
 * Iterates strokes in reverse draw order (last drawn = first to erase).
 */

import { HIT_TEST_TOLERANCE } from './constants';

/**
 * Squared distance from point to line segment (avoids sqrt for performance).
 * @param {number} px - Point x
 * @param {number} py - Point y
 * @param {number} x1 - Segment start x
 * @param {number} y1 - Segment start y
 * @param {number} x2 - Segment end x
 * @param {number} y2 - Segment end y
 * @returns {number} Squared distance
 */
function pointToSegmentDistanceSq(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) {
    return (px - x1) ** 2 + (py - y1) ** 2;
  }

  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));

  const projX = x1 + t * dx;
  const projY = y1 + t * dy;
  return (px - projX) ** 2 + (py - projY) ** 2;
}

/**
 * Find the first stroke (in reverse draw order) within tolerance of the point.
 * @param {{ x: number, y: number }} point - Logical coordinates
 * @param {Array<{ strokeId: string, points: Array<[number, number]> }>} strokes - Strokes array (draw order)
 * @param {number} [tolerance] - Max distance in logical units (default from constants)
 * @returns {string | null} strokeId of hit stroke, or null
 */
export function findStrokeAtPoint(point, strokes, tolerance = HIT_TEST_TOLERANCE) {
  if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
    return null;
  }
  if (!strokes || !Array.isArray(strokes) || strokes.length === 0) {
    return null;
  }
  const tol = typeof tolerance === 'number' && Number.isFinite(tolerance) ? Math.max(0, tolerance) : HIT_TEST_TOLERANCE;
  const tolSq = tol * tol;

  const px = point.x;
  const py = point.y;

  for (let i = strokes.length - 1; i >= 0; i--) {
    const stroke = strokes[i];
    if (!stroke?.strokeId || !stroke?.points?.length) continue;

    const points = stroke.points;

    if (points.length === 1) {
      const [x, y] = points[0];
      const dSq = (px - x) ** 2 + (py - y) ** 2;
      if (dSq <= tolSq) return stroke.strokeId;
    } else {
      for (let j = 0; j < points.length - 1; j++) {
        const [x1, y1] = points[j];
        const [x2, y2] = points[j + 1];
        if (
          typeof x1 !== 'number' || typeof y1 !== 'number' ||
          typeof x2 !== 'number' || typeof y2 !== 'number'
        ) {
          continue;
        }
        const dSq = pointToSegmentDistanceSq(px, py, x1, y1, x2, y2);
        if (dSq <= tolSq) return stroke.strokeId;
      }
    }
  }
  return null;
}
